/**
 * THE RISK-STAGE FORK (staleness gate, commit 5; backend commit 4).
 *
 * Opened when assess-risk refuses with RISK_CATEGORY_ATTESTATION_REQUIRED: the
 * RFP was generated under a category that is no longer active, and only the
 * buyer knows whether it has been issued. COPY IS VERBATIM from the ruling.
 *
 * THREE ARMS, EACH RECORDED server-side (POST /risk-category-attestation):
 *   as_issued  - generate Risk Intelligence for the RFP as issued
 *   regenerate - go back, reselect a current category, regenerate the RFP
 *   cancel     - return to the previous step without deciding (ruled
 *                2026-08-19: a two-way fork forces a buyer who is unsure
 *                whether the RFP went out to guess)
 * Dismissing the dialog (Escape, overlay) IS the cancel arm, and is recorded as
 * one - a buyer who closed the question was asked and did not decide.
 *
 * EXTENDED TO EVERY MASTER (Ashok ruling 3, 2026-09-16). The fork opens for a
 * retired ship-to location as well as a retired category, so the copy names
 * WHICHEVER references are stale - read off the refusal's stale_references -
 * and says "category" only when a category is what went stale. The category-
 * only wording is unchanged.
 */
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { describeForkReferences } from '@/lib/masterReferenceStaleness';

const RiskCategoryForkDialog = ({ open, busy = false, onDecision, references = [] }) => {
  const hasCodes = Array.isArray(references) && references.some((r) => r && r.code);
  const { sentence, kinds } = describeForkReferences(references);
  const kindSingular = hasCodes ? kinds : 'category';
  return (
  <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onDecision('cancel'); }}>
    <DialogContent className="sm:max-w-lg" data-testid="risk-category-fork-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          Risk Intelligence requires attention
        </DialogTitle>
        <DialogDescription className="text-zinc-700" data-testid="risk-fork-description">
          {hasCodes ? sentence : 'The category used to generate this RFP is no longer active in the procurement master.'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 text-sm text-zinc-700">
        <p data-testid="risk-fork-issued-copy">
          <strong>If this RFP has already been issued:</strong> You can proceed to generate Risk Intelligence for
          the RFP as issued, using the {kindSingular} under which it was generated.
        </p>
        <p data-testid="risk-fork-not-issued-copy">
          <strong>If this RFP has not yet been issued:</strong> Go back, select a current active {kindSingular} and
          regenerate the RFP before generating Risk Intelligence.
        </p>
      </div>
      {/* THE THREE ARMS STACK, AT EVERY WIDTH (2026-09-15). They used to sit
          in one row from the sm breakpoint up. Measured in headless Edge
          against the deployed CSS: the dialog is max-w-lg (512px) with p-6,
          a 464px content box; the three buttons' natural widths are 228.9 +
          240 + 77.5px (whitespace-nowrap, text-sm, px-4) and the footer
          carried gap-2 AND the primitive's sm:space-x-2, 32px between them -
          578px in one non-wrapping row, so "Generate for the RFP as issued"
          ended 114px past the content edge. The labels are ruled copy and
          are not shortened; widening the dialog would only move the problem
          to the viewports between sm and the new width. So the footer is a
          column, each button full width, primary first (DOM order is the
          visual order, so the tab order matches), and a label may wrap
          rather than overflow on the narrowest screens. */}
      <DialogFooter className="flex flex-col gap-2 sm:flex-col sm:justify-start sm:space-x-0">
        <Button
          disabled={busy}
          onClick={() => onDecision('as_issued')}
          className="w-full h-auto min-h-[2.25rem] whitespace-normal text-center bg-[#0055FF] hover:bg-[#0044CC] text-white"
          data-testid="risk-fork-as-issued"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Generate for the RFP as issued
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => onDecision('regenerate')}
          className="w-full h-auto min-h-[2.25rem] whitespace-normal text-center"
          data-testid="risk-fork-regenerate"
        >
          Go back and regenerate the RFP
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => onDecision('cancel')}
          className="w-full h-auto min-h-[2.25rem] whitespace-normal text-center"
          data-testid="risk-fork-cancel"
        >
          Cancel
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default RiskCategoryForkDialog;
